import React, { useState, useEffect, useCallback, useRef } from "react";
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Box, Typography, Avatar, IconButton, Button,
    TextField, InputAdornment, CircularProgress, Alert,
    List, ListItem, ListItemAvatar, ListItemText, ListItemSecondaryAction,
    Chip, Divider, Tooltip, TablePagination, FormControlLabel, Switch,
} from "@mui/material";
import GroupWorkIcon from "@mui/icons-material/GroupWork";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import PersonRemoveIcon from "@mui/icons-material/PersonRemove";
import PersonIcon from "@mui/icons-material/Person";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import RefreshIcon from "@mui/icons-material/Refresh";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

import { LOYALTY_API_BASE, SECTION_COLORS } from "../../constants";
import { loyaltyFetch } from "../../hooks/loyaltyFetch";
import { segmentErrorMessage } from "./segmentErrorMessage";
import { searchLoyaltyCustomersPaginated } from "../../utils/customerLookup";
import { fetchSegmentMembers } from "./segmentMembersApi";
import { canRefreshDynamicSegmentMembers, isUnomiOnlySegment } from "./segmentConditionsUtils";
import { resolveSegmentsUi, formatSegmentUiTemplate } from "../../utils/segmentUiCatalogDefaults";

const MEMBERS_PAGE_SIZE = 100;

const avatarLetter = (pid) => (pid ? pid.charAt(0).toUpperCase() : "?");

const avatarColor = (pid) => {
    if (!pid) return "#94a3b8";
    const hue = Math.abs(pid.split("").reduce((acc, c) => acc * 31 + c.charCodeAt(0), 0)) % 360;
    return `hsl(${hue}, 55%, 48%)`;
};

const fmtDate = (v) =>
    v ? new Date(v).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }) : "–";

/** Message contextuel dynamique — basé uniquement sur la réponse GET /members (pas needs_recompute carte). */
function resolveDynamicMembersHint(result, afterRefresh, canRefresh) {
    if (afterRefresh) return null;
    const hasMembers = (result.total ?? 0) > 0 || (result.items?.length ?? 0) > 0;
    if (!hasMembers) {
        if (!canRefresh) {
            return {
                severity: "info",
                message: "Aucun membre enregistré côté moteur. Pour un segment CDP only, le backend doit synchroniser les profils depuis Unomi (pas de recalcul AST loyalty).",
            };
        }
        return {
            severity: "info",
            message: result.membershipStale
                ? "Aucun membre pour l’instant. Le bouton « Actualiser » recalcule le segment selon ses conditions, puis recharge la liste."
                : "Aucun client ne correspond aux conditions du segment pour le moment.",
        };
    }
    if (result.membershipStale && canRefresh) {
        return {
            severity: "info",
            message: "Le dernier calcul est marqué obsolète côté moteur, mais des membres sont encore affichés. Utilisez « Actualiser » pour recalculer et mettre la liste à jour.",
        };
    }
    return null;
}

// ─── SearchCustomerPanel (static segments only) ─────────────────────────────

const SearchCustomerPanel = ({ brandName, existingIds, onAdd, segmentId, onMutationSuccess }) => {
    const [query, setQuery] = useState("");
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [adding, setAdding] = useState({});
    const [page, setPage] = useState(0);
    const [total, setTotal] = useState(0);
    const [mode, setMode] = useState("list");
    const debounceRef = useRef(null);
    const LIMIT = 20;

    const fetchCustomers = useCallback(
        async ({ q, nextPage }) => {
            setLoading(true);
            setError(null);
            try {
                const offset = (nextPage || 0) * LIMIT;
                const data = await searchLoyaltyCustomersPaginated(brandName, q, { limit: LIMIT, offset });
                setResults(Array.isArray(data.items) ? data.items : []);
                setTotal(Number.isFinite(data.total) ? data.total : (Array.isArray(data.items) ? data.items.length : 0));
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        },
        [brandName],
    );

    useEffect(() => {
        if (!brandName || mode !== "list") return undefined;
        fetchCustomers({ q: "", nextPage: page });
        return undefined;
    }, [brandName, mode, page, fetchCustomers]);

    const handleQueryChange = (e) => {
        const val = e.target.value;
        setQuery(val);
        setPage(0);
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            const hasQ = val.trim().length > 0;
            setMode(hasQ ? "search" : "list");
            fetchCustomers({ q: hasQ ? val : "", nextPage: 0 });
        }, 380);
    };

    const handleAdd = async (customer) => {
        setAdding((p) => ({ ...p, [customer.id]: true }));
        try {
            const res = await loyaltyFetch(
                `${LOYALTY_API_BASE}/admin/segments/${segmentId}/members`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json", "X-Brand": brandName },
                    body: JSON.stringify({ customer_id: customer.id }),
                },
            );
            if (!res.ok) {
                const e = await res.json().catch(() => null);
                throw new Error(segmentErrorMessage(res.status, e?.detail, "Ajout membre"));
            }
            const member = await res.json();
            onAdd(member, customer);
            onMutationSuccess?.();
        } catch (err) {
            setError(err.message);
        } finally {
            setAdding((p) => ({ ...p, [customer.id]: false }));
        }
    };

    return (
        <Box>
            <TextField
                fullWidth
                size="small"
                placeholder="Rechercher par Profile ID Unomi ou email…"
                helperText="Email : résolu via Unomi"
                value={query}
                onChange={handleQueryChange}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
                        </InputAdornment>
                    ),
                    endAdornment: loading ? (
                        <InputAdornment position="end">
                            <CircularProgress size={16} />
                        </InputAdornment>
                    ) : null,
                }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            />
            {error && <Alert severity="error" sx={{ mt: 1, borderRadius: 2, py: 0.5 }}>{error}</Alert>}
            {results.length > 0 && (
                <List dense disablePadding sx={{
                    mt: 1, maxHeight: 220, overflowY: "auto",
                    border: "1px solid #e8ecf1", borderRadius: 2, bgcolor: "#fff",
                }}>
                    {results.map((c, idx) => {
                        const alreadyIn = existingIds.has(c.id);
                        const isAdding = adding[c.id];
                        return (
                            <React.Fragment key={c.id}>
                                {idx > 0 && <Divider sx={{ mx: 1 }} />}
                                <ListItem sx={{ py: 0.75, px: 1.5 }}>
                                    <ListItemAvatar sx={{ minWidth: 36 }}>
                                        <Avatar sx={{ width: 28, height: 28, fontSize: 12, fontWeight: 700, bgcolor: avatarColor(c.profile_id) }}>
                                            {avatarLetter(c.profile_id)}
                                        </Avatar>
                                    </ListItemAvatar>
                                    <ListItemText
                                        primary={<Typography fontSize={12} fontWeight={600} color="#1a1a2e" noWrap>{c.profile_id}</Typography>}
                                        secondary={<Typography fontSize={10} color="text.secondary">{c.loyalty_status_name || c.loyalty_status || "–"} · {c.status_points ?? 0} pts</Typography>}
                                        sx={{ my: 0 }}
                                    />
                                    <ListItemSecondaryAction>
                                        {alreadyIn ? (
                                            <Chip label="Déjà membre" size="small"
                                                icon={<CheckCircleIcon sx={{ fontSize: "12px !important" }} />}
                                                sx={{ fontSize: 10, bgcolor: "#f0fdf4", color: "#16a34a", height: 22 }} />
                                        ) : (
                                            <Tooltip title="Ajouter au segment">
                                                <span>
                                                    <IconButton size="small" disabled={isAdding} onClick={() => handleAdd(c)}
                                                        sx={{ color: SECTION_COLORS.segments.main, bgcolor: `${SECTION_COLORS.segments.main}12`, width: 26, height: 26 }}>
                                                        {isAdding ? <CircularProgress size={14} color="inherit" /> : <PersonAddIcon sx={{ fontSize: 14 }} />}
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        )}
                                    </ListItemSecondaryAction>
                                </ListItem>
                            </React.Fragment>
                        );
                    })}
                </List>
            )}
            {!loading && query.trim() && results.length === 0 && (
                <Typography fontSize={12} color="text.secondary" mt={1} textAlign="center">
                    Aucun client trouvé pour « {query} »
                </Typography>
            )}
            {mode === "list" && total > LIMIT && (
                <TablePagination component="div" count={total} page={page} onPageChange={(_, p) => setPage(p)}
                    rowsPerPage={LIMIT} rowsPerPageOptions={[LIMIT]} sx={{ mt: 0.5 }} />
            )}
        </Box>
    );
};

// ─── Members list ─────────────────────────────────────────────────────────────

const MembersList = ({
    members,
    profiles,
    removing,
    onRemove,
    provider,
    readOnly = false,
    showVerify = false,
    cdpWithoutEngineHint = "Profil CDP sans fiche loyalty",
}) => {
    if (members.length === 0) {
        return (
            <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={4} gap={1}
                sx={{ border: "1px dashed #e2e8f0", borderRadius: 2, bgcolor: "#fafbfd" }}>
                <PeopleAltIcon sx={{ fontSize: 36, color: "#cbd5e1" }} />
                <Typography fontSize={12} color="text.secondary">Aucun membre sur cette page</Typography>
            </Box>
        );
    }

    return (
        <List dense disablePadding sx={{
            maxHeight: 320, overflowY: "auto",
            border: "1px solid #e8ecf1", borderRadius: 2, bgcolor: "#fff",
        }}>
            {members.map((m, idx) => {
                const prof = profiles[m.customer_id] || null;
                const pid = m.profile_id || prof?.profile_id || m.customer_id || "—";
                const removeKey = m.customer_id || pid;
                const isRemoving = removing[removeKey];
                const noEngine = m.customer_found_in_engine === false;
                const mismatch = showVerify && m.matches_conditions === false;

                return (
                    <React.Fragment key={`${removeKey}-${idx}`}>
                        {idx > 0 && <Divider sx={{ mx: 1 }} />}
                        <ListItem sx={{ py: 0.75, px: 1.5, bgcolor: mismatch ? "#fff7ed" : undefined }}>
                            <ListItemAvatar sx={{ minWidth: 36 }}>
                                <Avatar sx={{ width: 28, height: 28, fontSize: 12, fontWeight: 700, bgcolor: avatarColor(pid) }}>
                                    {avatarLetter(pid)}
                                </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                                primary={<Typography fontSize={12} fontWeight={600} color="#1a1a2e" noWrap>{pid}</Typography>}
                                secondary={
                                    <Box display="flex" alignItems="center" gap={0.5} flexWrap="wrap">
                                        <Typography fontSize={10} color="text.secondary">
                                            {m.computed_at ? `Calculé ${fmtDate(m.computed_at)}` : m.created_at ? `Ajouté ${fmtDate(m.created_at)}` : "—"}
                                        </Typography>
                                        {m.source && (
                                            <Chip label={m.source} size="small"
                                                sx={{
                                                    fontSize: 9, height: 16,
                                                    bgcolor: m.source === "DYNAMIC" ? "#f0fdf4" : "#eff6ff",
                                                    color: m.source === "DYNAMIC" ? "#15803d" : "#1d4ed8",
                                                }} />
                                        )}
                                        {noEngine && (
                                            <Chip label={cdpWithoutEngineHint} size="small"
                                                sx={{ fontSize: 9, height: 16, bgcolor: "#fff7ed", color: "#c2410c" }} />
                                        )}
                                        {showVerify && m.matches_conditions === true && (
                                            <Chip label="OK conditions" size="small"
                                                sx={{ fontSize: 9, height: 16, bgcolor: "#f0fdf4", color: "#16a34a" }} />
                                        )}
                                        {showVerify && m.matches_conditions === false && (
                                            <Chip label="Hors conditions" size="small" icon={<WarningAmberIcon sx={{ fontSize: 12 }} />}
                                                sx={{ fontSize: 9, height: 16, bgcolor: "#fef2f2", color: "#b91c1c" }} />
                                        )}
                                    </Box>
                                }
                                sx={{ my: 0 }}
                            />
                            {!readOnly && m.customer_id && (
                                <ListItemSecondaryAction>
                                    <Tooltip title="Retirer du segment">
                                        <span>
                                            <IconButton size="small" disabled={isRemoving}
                                                onClick={() => onRemove(removeKey, m.customer_id)}
                                                sx={{ color: "#DC2626", bgcolor: "#fef2f2", width: 26, height: 26 }}>
                                                {isRemoving ? <CircularProgress size={14} color="inherit" /> : <PersonRemoveIcon sx={{ fontSize: 14 }} />}
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                </ListItemSecondaryAction>
                            )}
                        </ListItem>
                    </React.Fragment>
                );
            })}
        </List>
    );
};

// ─── Main dialog ──────────────────────────────────────────────────────────────

const ManageSegmentMembersDialog = ({ open, segment, brandName, onClose, usesUnomi = false, segmentsUi: segmentsUiProp, onChanged }) => {
    const segmentsUi = segmentsUiProp ?? resolveSegmentsUi(null);
    const isDynamic = Boolean(segment?.is_dynamic);
    const canManage = !isDynamic;
    const cdpOnly = isUnomiOnlySegment(segment);
    const canRefresh = canRefreshDynamicSegmentMembers(segment);

    const [members, setMembers] = useState([]);
    const [memberTotal, setMemberTotal] = useState(0);
    const [membersPage, setMembersPage] = useState(0);
    const [profiles, setProfiles] = useState({});
    const [loadingMembers, setLoadingMembers] = useState(false);
    const [membersError, setMembersError] = useState(null);
    const [removing, setRemoving] = useState({});
    const [syncing, setSyncing] = useState(false);
    const [syncMessage, setSyncMessage] = useState(null);

    const [membershipStale, setMembershipStale] = useState(false);
    const [lastComputedAt, setLastComputedAt] = useState(null);
    const [refreshed, setRefreshed] = useState(false);
    const [verified, setVerified] = useState(false);
    const [pageMismatchCount, setPageMismatchCount] = useState(0);
    const [verifyEnabled, setVerifyEnabled] = useState(false);
    const [membersHint, setMembersHint] = useState(null);

    const existingIds = new Set(members.map((m) => m.customer_id).filter(Boolean));

    const loadMembers = useCallback(async ({ refresh = false, verify = false, page = 0 } = {}) => {
        if (!segment?.id || !brandName) return;
        setLoadingMembers(true);
        setMembersError(null);
        try {
            const result = await fetchSegmentMembers({
                segmentId: segment.id,
                brandName,
                limit: MEMBERS_PAGE_SIZE,
                offset: page * MEMBERS_PAGE_SIZE,
                refresh: canRefresh && refresh,
                verify: canRefresh && verify,
            });
            setMembers(result.items);
            setMemberTotal(result.total);
            setMembersPage(page);
            setMembershipStale(result.membershipStale);
            setLastComputedAt(result.lastComputedAt);
            setRefreshed(result.refreshed);
            setVerified(result.verified);
            setPageMismatchCount(result.pageMismatchCount);
            setMembersHint(isDynamic ? resolveDynamicMembersHint(result, refresh, canRefresh) : null);
        } catch (err) {
            setMembersError(err.message);
        } finally {
            setLoadingMembers(false);
        }
    }, [segment?.id, brandName, isDynamic, canRefresh]);

    useEffect(() => {
        if (!open) {
            setMembers([]);
            setProfiles({});
            setMembersPage(0);
            setVerifyEnabled(false);
            setMembersHint(null);
            setSyncMessage(null);
            return;
        }
        loadMembers({ refresh: false, verify: false, page: 0 });
    }, [open, loadMembers]);

    const handleRefresh = () => {
        if (!canRefresh) return;
        loadMembers({ refresh: true, verify: verifyEnabled, page: membersPage });
    };

    const handleVerifyToggle = (_, checked) => {
        setVerifyEnabled(checked);
        loadMembers({ refresh: false, verify: checked, page: membersPage });
    };

    const handleMembersPageChange = (_, newPage) => {
        loadMembers({ refresh: false, verify: verifyEnabled, page: newPage });
    };

    const handleRemove = async (removeKey, customerId) => {
        if (!customerId || !canManage) return;
        setRemoving((p) => ({ ...p, [removeKey]: true }));
        try {
            const res = await loyaltyFetch(
                `${LOYALTY_API_BASE}/admin/segments/${segment.id}/members/bulk-delete`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json", "X-Brand": brandName },
                    body: JSON.stringify({ customer_ids: [customerId] }),
                },
            );
            if (!res.ok) {
                const e = await res.json().catch(() => null);
                throw new Error(segmentErrorMessage(res.status, e?.detail, "Retrait membre"));
            }
            await loadMembers({ refresh: false, verify: verifyEnabled, page: membersPage });
            onChanged?.();
        } catch (err) {
            setMembersError(err.message);
        } finally {
            setRemoving((p) => ({ ...p, [removeKey]: false }));
        }
    };

    const handleSyncUnomi = async () => {
        if (!segment?.id || !brandName) return;
        setSyncing(true);
        setSyncMessage(null);
        try {
            const res = await loyaltyFetch(
                `${LOYALTY_API_BASE}/admin/segments/${segment.id}/sync-unomi`,
                { method: "POST", headers: { "X-Brand": brandName } },
            );
            const data = await res.json().catch(() => null);
            if (!res.ok) throw new Error(segmentErrorMessage(res.status, data?.detail, "Sync Unomi"));
            setSyncMessage(`Unomi synchronisé (${data?.profileCount ?? "?"} profil(s))`);
            await loadMembers({ refresh: false, verify: false, page: 0 });
            onChanged?.();
        } catch (err) {
            setSyncMessage(err.message);
        } finally {
            setSyncing(false);
        }
    };

    const handleAdd = (member, customerOut) => {
        if (customerOut) setProfiles((p) => ({ ...p, [member.customer_id]: customerOut }));
        loadMembers({ refresh: false, verify: false, page: 0 });
        onChanged?.();
    };

    if (!segment) return null;

    const subtitle = isDynamic
        ? `Segment dynamique · ${segment.provider === "UNOMI" || usesUnomi ? "Unomi" : "INTERNAL"} · lecture moteur`
        : `Segment statique · ${segment.provider === "UNOMI" || usesUnomi ? "Unomi (liste manuelle)" : "INTERNAL"}`;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
            PaperProps={{ sx: { borderRadius: 3, maxHeight: "92vh" } }}>
            <DialogTitle sx={{
                borderBottom: "2px solid #f1f3f5", px: 3, py: 2,
                display: "flex", justifyContent: "space-between", alignItems: "center",
            }}>
                <Box display="flex" alignItems="center" gap={1}>
                    <Avatar sx={{ width: 32, height: 32, background: SECTION_COLORS.segments.gradient }}>
                        <GroupWorkIcon sx={{ fontSize: 18 }} />
                    </Avatar>
                    <Box>
                        <Typography variant="h6" fontFamily="Montserrat, sans-serif" fontWeight={700} color="#1a1a2e" fontSize={15}>
                            Membres — {segment.name}
                        </Typography>
                        <Typography fontSize={11} color="text.secondary">{subtitle}</Typography>
                    </Box>
                </Box>
                <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
            </DialogTitle>

            <DialogContent sx={{ pt: "20px !important", px: 3, pb: 2, display: "flex", flexDirection: "column", gap: 2 }}>
                <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" px={2} py={1}
                    sx={{ borderRadius: 2, bgcolor: `${SECTION_COLORS.segments.main}08`, border: `1px solid ${SECTION_COLORS.segments.main}20` }}>
                    <PeopleAltIcon sx={{ fontSize: 18, color: SECTION_COLORS.segments.main }} />
                    <Typography fontSize={13} fontWeight={600} color={SECTION_COLORS.segments.main}>
                        {memberTotal} membre{memberTotal !== 1 ? "s" : ""}
                    </Typography>
                    {lastComputedAt && (
                        <Typography fontSize={11} color="text.secondary">
                            · Dernier calcul {fmtDate(lastComputedAt)}
                        </Typography>
                    )}
                    {loadingMembers && <CircularProgress size={14} />}
                    <Box ml="auto" display="flex" gap={1} flexWrap="wrap" alignItems="center">
                        {isDynamic && canRefresh && (
                            <>
                                <Tooltip title={segmentsUi.members.refreshTooltip}>
                                    <span>
                                        <Button size="small" variant="outlined" disabled={loadingMembers}
                                            startIcon={<RefreshIcon />}
                                            onClick={handleRefresh}
                                            sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}>
                                            {segmentsUi.members.refreshLabel}
                                        </Button>
                                    </span>
                                </Tooltip>
                                <Tooltip title={segmentsUi.members.verifyTooltip}>
                                    <FormControlLabel
                                        control={
                                            <Switch size="small" checked={verifyEnabled}
                                                onChange={handleVerifyToggle} disabled={loadingMembers} />
                                        }
                                        label={<Typography fontSize={12}>{segmentsUi.members.verifyLabel}</Typography>}
                                    />
                                </Tooltip>
                            </>
                        )}
                        {canManage && (segment.provider === "UNOMI" || usesUnomi) && (
                            <Button size="small" variant="outlined" disabled={syncing}
                                onClick={handleSyncUnomi}
                                sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}>
                                {syncing ? "Sync…" : segmentsUi.members.syncUnomiLabel}
                            </Button>
                        )}
                    </Box>
                </Box>

                {isDynamic && (
                    <Typography fontSize={11} color="text.secondary" px={0.5}>
                        {cdpOnly ? segmentsUi.members.dynamicUnomiOnlyHint : segmentsUi.members.dynamicAstHint}
                    </Typography>
                )}

                {isDynamic && cdpOnly && segmentsUi.members.unomiOnlyBanner && (
                    <Alert severity="info" icon={<WarningAmberIcon />} sx={{ borderRadius: 2 }}>
                        {segmentsUi.members.unomiOnlyBanner}
                    </Alert>
                )}

                {membersHint && (
                    <Alert severity={membersHint.severity} sx={{ borderRadius: 2 }}>
                        {membersHint.message}
                    </Alert>
                )}

                {refreshed && (
                    <Alert severity="success" sx={{ borderRadius: 2 }}>{segmentsUi.members.refreshedSuccess}</Alert>
                )}

                {verified && pageMismatchCount > 0 && (
                    <Alert severity="warning" icon={<FactCheckIcon />} sx={{ borderRadius: 2 }}>
                        {formatSegmentUiTemplate(segmentsUi.members.verifyMismatch, { count: pageMismatchCount })}
                    </Alert>
                )}

                {verified && pageMismatchCount === 0 && members.length > 0 && (
                    <Alert severity="success" icon={<FactCheckIcon />} sx={{ borderRadius: 2 }}>
                        {segmentsUi.members.verifyOk}
                    </Alert>
                )}

                {syncMessage && (
                    <Alert severity={syncMessage.includes("Erreur") || syncMessage.includes("HTTP") ? "error" : "success"} sx={{ borderRadius: 2 }}>
                        {syncMessage}
                    </Alert>
                )}

                {membersError && <Alert severity="error" sx={{ borderRadius: 2 }}>{membersError}</Alert>}

                {canManage && (
                    <>
                        <Box>
                            <Typography fontSize={12} fontWeight={700} color="#334155" mb={1} display="flex" alignItems="center" gap={0.5}>
                                <PersonAddIcon sx={{ fontSize: 14 }} /> Ajouter un client
                            </Typography>
                            <SearchCustomerPanel
                                brandName={brandName}
                                existingIds={existingIds}
                                segmentId={segment.id}
                                onAdd={handleAdd}
                                onMutationSuccess={() => onChanged?.()}
                            />
                        </Box>
                        <Divider sx={{ borderColor: "#f1f3f5" }} />
                    </>
                )}

                <Box>
                    <Typography fontSize={12} fontWeight={700} color="#334155" mb={1} display="flex" alignItems="center" gap={0.5}>
                        <PersonIcon sx={{ fontSize: 14 }} />
                        {isDynamic ? "Membres (lecture seule)" : "Membres actuels"}
                    </Typography>
                    {loadingMembers ? (
                        <Box display="flex" justifyContent="center" py={3}><CircularProgress size={24} /></Box>
                    ) : (
                        <>
                            <MembersList
                                members={members}
                                profiles={profiles}
                                removing={removing}
                                onRemove={handleRemove}
                                provider={segment.provider}
                                readOnly={!canManage}
                                showVerify={verified}
                                cdpWithoutEngineHint={segmentsUi.members.cdpWithoutEngineHint}
                            />
                            {memberTotal > MEMBERS_PAGE_SIZE && (
                                <TablePagination
                                    component="div"
                                    count={memberTotal}
                                    page={membersPage}
                                    onPageChange={handleMembersPageChange}
                                    rowsPerPage={MEMBERS_PAGE_SIZE}
                                    rowsPerPageOptions={[MEMBERS_PAGE_SIZE]}
                                    sx={{ borderTop: "1px solid #f0f0f0" }}
                                />
                            )}
                        </>
                    )}
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #e0e0e0" }}>
                <Button onClick={onClose} sx={{ color: "text.secondary", textTransform: "none" }}>Fermer</Button>
            </DialogActions>
        </Dialog>
    );
};

export default ManageSegmentMembersDialog;
